/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.sprudel.ui

import io.peekandpoke.klang.sprudel.lang.parser.MnNode
import io.peekandpoke.klang.ui.HoverPopupCtrl
import io.peekandpoke.klang.ui.KlangUiTool
import io.peekandpoke.klang.ui.KlangUiToolContext
import io.peekandpoke.klang.ui.KlangUiToolEmbeddable
import io.peekandpoke.klang.ui.codetools.CodeToolModal
import io.peekandpoke.kraft.components.Ctx
import io.peekandpoke.kraft.components.comp
import io.peekandpoke.kraft.modals.ModalsManager.Companion.modals
import io.peekandpoke.kraft.popups.PopupsManager.Companion.popups
import io.peekandpoke.kraft.vdom.VDom
import io.peekandpoke.ultra.html.css
import io.peekandpoke.ultra.html.key
import io.peekandpoke.ultra.html.onClick
import io.peekandpoke.ultra.html.onInput
import io.peekandpoke.ultra.semanticui.SemanticIconFn
import io.peekandpoke.ultra.semanticui.icon
import io.peekandpoke.ultra.semanticui.ui
import kotlinx.css.Align
import kotlinx.css.Color
import kotlinx.css.Display
import kotlinx.css.FlexDirection
import kotlinx.css.FontWeight
import kotlinx.css.Padding
import kotlinx.css.alignItems
import kotlinx.css.borderRadius
import kotlinx.css.color
import kotlinx.css.display
import kotlinx.css.em
import kotlinx.css.flexDirection
import kotlinx.css.flexGrow
import kotlinx.css.fontFamily
import kotlinx.css.fontSize
import kotlinx.css.fontWeight
import kotlinx.css.gap
import kotlinx.css.marginLeft
import kotlinx.css.minHeight
import kotlinx.css.minWidth
import kotlinx.css.padding
import kotlinx.css.px
import kotlinx.css.vh
import kotlinx.css.vw
import kotlinx.html.FlowContent
import kotlinx.html.InputType
import kotlinx.html.Tag
import kotlinx.html.div
import kotlinx.html.input
import kotlinx.html.span

// ── Tool factory ──────────────────────────────────────────────────────────────

/**
 * A [KlangUiTool] that edits mini-notation pattern strings.
 *
 * The user edits the raw mini-notation string in a text field.
 * Clicking into an atom token reveals a modifier panel and, when [atomTool] is set,
 * an inline or modal sub-tool for editing the atom's value.
 */
class SprudelMiniNotationEditorTool(
    private val atomTool: KlangUiTool? = null,
) : KlangUiToolEmbeddable {
    // Distinct from the atom tool's own title: the Sprudel tab's keys list both, with the same icon.
    override val title: String = atomTool?.title?.let { "$it (mini-notation)" } ?: "Mini-Notation Editor"

    override val iconFn: SemanticIconFn = atomTool?.iconFn ?: { code }

    override val editsSequence: Boolean = true

    override fun FlowContent.render(ctx: KlangUiToolContext) {
        SprudelMiniNotationEditorComp(toolCtx = ctx, atomTool = atomTool, embedded = false)
    }

    override fun FlowContent.renderEmbedded(ctx: KlangUiToolContext) {
        SprudelMiniNotationEditorComp(toolCtx = ctx, atomTool = atomTool, embedded = true)
    }
}

@Suppress("FunctionName")
private fun Tag.SprudelMiniNotationEditorComp(toolCtx: KlangUiToolContext, atomTool: KlangUiTool?, embedded: Boolean) =
    comp(SprudelMiniNotationEditorComp.Props(toolCtx, atomTool, embedded)) { SprudelMiniNotationEditorComp(it) }

// ── Component ─────────────────────────────────────────────────────────────────

private class SprudelMiniNotationEditorComp(ctx: Ctx<Props>) : MnPatternEditorBase<SprudelMiniNotationEditorComp.Props>(ctx) {

    data class Props(
        override val toolCtx: KlangUiToolContext,
        val atomTool: KlangUiTool?,
        override val embedded: Boolean,
    ) : BaseProps

    private val infoPopup = HoverPopupCtrl(popups)

    // ── Render ────────────────────────────────────────────────────────────────

    override fun VDom.render() {
        val atom = resolveCurrentAtom()
        val isSynthetic = isSyntheticAtom(atom)

        ui.segment {
            css {
                if (!props.embedded) {
                    minWidth = 60.vw
                    minHeight = 60.vh
                }
                display = Display.flex
                flexDirection = FlexDirection.column
            }

            ui.small.header {
                val atomTitle = props.atomTool?.title
                if (atomTitle != null) {
                    +atomTitle
                    span {
                        css {
                            fontWeight = FontWeight.normal
                            fontSize = 1.em
                            color = Color(laf.textSecondary)
                            marginLeft = 12.px
                        }
                        +"Mini Notation"
                    }
                } else {
                    +"Mini Notation"
                }
            }

            mnPatternTextInput(laf, text, if (isSynthetic) null else atom, parseError) { newText, cursor ->
                text = newText
                cursorOffset = cursor
                lastAtom = lastAtom?.let { a -> pattern?.let { p -> findAtomById(p, a.id) } }
                liveUpdate()
            }

            ui.divider {}
            if (!isSynthetic) {
                mnModifierPanel(laf, atom) { updated -> updateNode(atom, updated) }
                ui.divider {}
            }
            renderAtomPanel(atom)

            div { css { flexGrow = 1.0 } }

            // The bar, under a divider; in a panel there is none
            if (!props.embedded) {
                ui.divider {}
            }

            renderBottomBar()
        }
    }

    // ── Atom value panel ──────────────────────────────────────────────────────

    private fun FlowContent.renderAtomPanel(atom: MnNode.Atom) {
        when (val atomTool = props.atomTool) {
            null -> renderAtomValueInput(atom)
            is KlangUiToolEmbeddable -> renderEmbeddedAtomTool(atom, atomTool)
            else -> renderAtomModalButton(atom, atomTool)
        }
    }

    private fun FlowContent.renderAtomValueInput(atom: MnNode.Atom) {
        div {
            css { display = Display.flex; alignItems = Align.center; gap = 8.px }
            span {
                css { fontSize = 12.px; color = Color("#666"); fontWeight = FontWeight.w600; minWidth = 60.px }
                +"Value"
            }
            input {
                type = InputType.text
                value = atom.value
                css {
                    fontFamily = "monospace"
                    fontSize = 14.px
                    padding = Padding(4.px, 8.px)
                    borderRadius = 4.px
                    put("border", "1px solid #ccc")
                }
                onInput { e ->
                    val v = e.target?.asDynamic()?.value as? String ?: return@onInput
                    updateNode(atom, atom.copy(value = v))
                }
            }
        }
    }

    private fun atomSubCtx(atom: MnNode.Atom, onCancel: () -> Unit, onCommit: (String) -> Unit) =
        props.toolCtx.copy(
            currentValue = "\"${atom.value}\"",
            onCancel = onCancel,
            onCommit = onCommit,
            // Atom editing is SCALAR by definition: never hand the whole-call view to the
            // atom tool, or it would rewrite the host call instead of this one atom (C0.3).
            call = null,
        )

    private fun FlowContent.renderEmbeddedAtomTool(atom: MnNode.Atom, atomTool: KlangUiToolEmbeddable) {
        val subCtx = atomSubCtx(atom, onCancel = {}, onCommit = { newVal ->
            updateNode(atom, atom.copy(value = newVal.trim().removeSurrounding("\"")))
        })
        div {
            key = "atom-tool-$resetVersion-$cursorOffset"
            with(atomTool) { renderEmbedded(subCtx) }
        }
    }

    private fun FlowContent.renderAtomModalButton(atom: MnNode.Atom, atomTool: KlangUiTool) {
        ui.basic.small.button {
            onClick {
                modals.show<Unit> { handle ->
                    CodeToolModal(handle) {
                        val subCtx = atomSubCtx(
                            atom,
                            onCancel = { handle.close() },
                            onCommit = { newVal ->
                                updateNode(atom, atom.copy(value = newVal.trim().removeSurrounding("\"")))
                                handle.close()
                            },
                        )
                        with(atomTool) { render(subCtx) }
                    }
                }
            }
            icon.edit()
            +"Edit '${atom.value}'…"
        }
    }
}
